import React, { useRef } from 'react'
import { useParams } from 'react-router-dom'
import DialogCloseButton from '@/components/dialog/DialogCloseButton'
import { Box, Button, Dialog, DialogActions, DialogContent, Grid, Typography } from '@mui/material'
import { FormFileInput } from '../form/FormFileInput'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { getUploadContractSchema, useCompanyUploadContractMutation } from '@/@data'
import InputDateField from '../common/input/InputDateField'
import { useTranslator } from '@/hooks/useTranslator'
import { useNotification } from '@/hooks/useNotification'

interface UploadContractDialogProps {
  open: boolean
  onClose: () => void
  refetch: () => void
}

export const UploadContractDialog = ({ open, onClose, refetch }: UploadContractDialogProps) => {
  const { __ } = useTranslator()
  const hiddenFileInputRef = useRef<HTMLInputElement>(null)
  const { id } = useParams()
  const n = useNotification()

  const { mutate: uploadContract } = useCompanyUploadContractMutation()

  const methods = useForm<any>({
    defaultValues: {
      contract_file: null,
      start_date: '',
      end_date: ''
    },
    mode: 'onChange',
    resolver: yupResolver(getUploadContractSchema(__))
  })

  const handleChooseClick = () => {
    hiddenFileInputRef.current?.click()
  }

  const handleHiddenFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files

    if (files && files.length > 0) {
      methods.setValue('contract_file', files, {
        shouldDirty: true,
        shouldValidate: true
      })
    }
  }

  const onSubmit = (data: any) => {
    const formData = new FormData()

    if (data.contract_file instanceof FileList && data.contract_file.length > 0) {
      formData.append('contract_file', data.contract_file[0])
    }

    formData.append('start_date', data.start_date)
    formData.append('end_date', data.end_date)

    uploadContract(
      {
        companyId: Number(id),
        data: formData
      },
      {
        onSuccess: () => {
          n.success(__('Upload contract successfully'))
          onClose()
          refetch()
        },
        onError: error => {
          n.error(error.message)
        }
      }
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='sm'
      scroll='body'
      fullWidth
      closeAfterTransition={false}
      sx={{
        '& .MuiDialog-paper': { overflow: 'visible', maxWidth: '700px', borderRadius: '32px' }
      }}
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <DialogContent className='p-0 m-0' sx={{ padding: 0 }}>
            <DialogCloseButton onClick={onClose} disableRipple>
              <i className='tabler-x' />
            </DialogCloseButton>

            <Box className='flex flex-col gap-6 p-6'>
              <Typography className='text-[18px] leading-[28px] font-medium'>{__('Upload Active Contract')}</Typography>
              <Grid container spacing={4}>
                <Grid size={{ xs: 12, md: 10 }}>
                  <FormFileInput
                    name='contract_file'
                    label={__('File')}
                    placeholder={__('No file chosen')}
                    required
                    fullWidth
                    control={methods.control}
                    onClear={() => methods.setValue('contract_file', null)}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 2 }} className='flex justify-center items-end'>
                  <Button
                    variant='contained'
                    className='bg-[#DEE3FD] shadow-none text-[#3551F2] rounded-full'
                    onClick={handleChooseClick}
                  >
                    {__('Choose')}
                  </Button>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Controller
                    control={methods.control}
                    name='start_date'
                    render={({ field, fieldState }) => (
                      <InputDateField
                        {...field}
                        label={__('Contract Start Date')}
                        placeholder={__('Start Date')}
                        required
                        fullWidth
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Controller
                    control={methods.control}
                    name='end_date'
                    render={({ field, fieldState }) => (
                      <InputDateField
                        {...field}
                        label={__('Contract End Date')}
                        placeholder={__('End Date')}
                        required
                        fullWidth
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions className='flex justify-end'>
            <Button
              variant='contained'
              className='bg-[#3551F2] text-[#fff] rounded-full'
              type='submit'
              disabled={!methods.formState.isValid}
            >
              {__('Upload')}
            </Button>
            <Button onClick={onClose} variant='outlined' className='text-[#3551F2] rounded-full'>
              {__('Cancel')}
            </Button>
          </DialogActions>
          <input
            ref={hiddenFileInputRef}
            type='file'
            style={{ display: 'none' }}
            onChange={handleHiddenFileChange}
            accept='.pdf,.doc,.docx'
          />
        </form>
      </FormProvider>
    </Dialog>
  )
}
